import React, { memo, useMemo, useState, useCallback } from "react";
import {
  Multiselect,
  MultiselectOption,
} from "../../../components/ui/documentsMultiselect";
import { AuditSession, Procedure } from "../../../models/audit/auditSession";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Trash2, AlertCircle } from "lucide-react";
import { Checkbox } from "../../../components/ui/checkbox";
import { AnalyticsInputToggle } from "../../../components/analytics/AnalyticsInputToggle";
import { AnalyticsParametersCell } from "../../../components/analytics/AnalyticsParametersCell";
import { SampleReachCount } from "../../../components/analytics/SampleReachCount";
import type { DocumentRowAnalytics } from "../../../hooks/audit/useDocumentRowAnalytics";
import { documentsGridColumns } from "./documentsGrid";
import { documentKey, type BoundDocumentId } from "../../../lib/analyticsFeeds";

interface DocumentRowProps {
  doc: AuditSession["documents"][0] & {
    category?: Procedure["requiredDocuments"][0];
  };
  isSelected: boolean;
  isChecked: boolean;
  onCheckboxToggle: (documentId: string, event: React.MouseEvent) => void;
  sampleValues: string[];
  categoryOptions: { value: string; label: string }[];
  onDocumentSelect: (doc: AuditSession["documents"][0]) => void;
  onSampleChange: (documentId: string, sampleIds: string[]) => void;
  onCategoryChange: (documentId: string, categoryIds: string[]) => void;
  sampleOptions: (MultiselectOption & { sampleNumber?: number })[];
  auditSession?: AuditSession | null;
  onRemoveDocument?: (documentId: string) => Promise<void> | void;
  analytics?: DocumentRowAnalytics;
  /**
   * The session's file names, for the parameter editor's account of what fills
   * each input. Required rather than optional even though only the analytics
   * cell reads it: a row that quietly rendered without it would name none of
   * the files it is reporting on.
   */
  documentNames: ReadonlyMap<BoundDocumentId, string>;
}

// File type badge color mapping
const getFileTypeBadgeColor = (fileType: string) => {
  const type = fileType?.toLowerCase();
  switch (type) {
    case "pdf":
      return "bg-white border-gray-200";
    case "xlsx":
    case "xlsm":
    case "xls":
      return "bg-green-100 text-green-800 border-green-200";
    case "png":
    case "jpg":
    case "jpeg":
      return "bg-blue-100 text-blue-800 border-blue-200";
    case "mp4":
    case "mov":
      return "bg-purple-100 text-purple-800 border-purple-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

const DocumentRow: React.FC<DocumentRowProps> = memo(
  ({
    doc,
    isSelected,
    isChecked,
    onCheckboxToggle,
    sampleValues,
    categoryOptions,
    onDocumentSelect,
    onSampleChange,
    onCategoryChange,
    sampleOptions,
    auditSession,
    onRemoveDocument,
    analytics,
    documentNames,
  }) => {
    // Null for a row naming no document, which is the whole of what the binding
    // surfaces need to know: none of them can be operated on a file they cannot
    // name back to the route that would store it.
    const boundDocumentId = documentKey(doc);
    // The row's plain document id, for deletion, selection and the sample and
    // category writes, none of which keys a binding. Derived from the same call
    // rather than choosing a half again here, and the fallback stands on
    // emptiness being the whole of what `isBoundDocumentId` tests: the only id
    // `documentKey` declines is the one substituted here, so these callbacks
    // carry the string they would have carried anyway. A predicate that grew a
    // real check — a shape, a prefix — would start handing `""` to every one of
    // them for rows with perfectly good ids, and nothing here would fail.
    const documentId = boundDocumentId ?? "";
    const fileType = doc.fetchableDocument?.fileType?.toUpperCase() || "FILE";
    const fileName = doc.fetchableDocument?.fileName || doc.name || "Untitled";
    const documentState = doc.fetchableDocument?.state;
    const isParsed = doc.fetchableDocument?.isParsed || false;

    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Determine error message for failed documents
    const failedMessage = useMemo(() => {
      if (documentState !== "failed") return null;

      if (!isParsed) {
        return {
          message: "Problems during processing of this document",
          severity: "error" as const,
        };
      }
      return {
        message: "Problems matching document to samples, must manually match",
        severity: "warning" as const,
      };
    }, [documentState, isParsed]);

    const handleDelete = useCallback(async () => {
      if (!onRemoveDocument) return;
      try {
        setIsDeleting(true);
        await onRemoveDocument(documentId);
        setIsConfirmOpen(false);
      } finally {
        setIsDeleting(false);
      }
    }, [documentId, onRemoveDocument]);

    const uniqueSampleValues = useMemo(() => {
      const seen = new Set<string>();
      const uniqueValues = [];
      for (const value of sampleValues) {
        if (!seen.has(value)) {
          seen.add(value);
          uniqueValues.push(value);
        }
      }
      return uniqueValues;
    }, [sampleValues]);

    // Get current assignments from doc.fetchableDocument.documentAssignments
    // with fallback to searching through matchItems (similar to AuditDocumentVerification)
    const currentAssignments = useMemo(() => {
      // First try new structure
      if (doc.fetchableDocument?.documentAssignments?.length) {
        return doc.fetchableDocument.documentAssignments.map(
          (assignment) => assignment.id,
        );
      }

      // Fallback: search through all matchItems to find assignments for this document
      if (auditSession?.sourceRows) {
        const assignments: string[] = [];

        for (const sourceRow of auditSession.sourceRows) {
          for (const matchItem of sourceRow.matchItems || []) {
            if (matchItem.sourceDocument.id === documentId) {
              // Get procedure assignments from the matchItem
              if (matchItem.assignments) {
                assignments.push(
                  ...matchItem.assignments.map((a) => a.procedureDocumentId),
                );
              }
            }
          }
        }

        // Dedupe assignments
        return [...new Set(assignments)];
      }

      return [];
    }, [
      doc.fetchableDocument?.documentAssignments,
      auditSession?.sourceRows,
      documentId,
    ]);

    // One expression drives both the track list and the Sample Match cell,
    // because the grid is one track wider exactly when that cell renders.
    const procedureBasis =
      auditSession?.procedure.procedureBasis === "sample" ? "sample" : "direct";

    return (
      <div
        className={`grid ${documentsGridColumns(analytics !== undefined, procedureBasis)} items-center border-b hover:bg-stone-50 transition-colors cursor-pointer ${isSelected ? "bg-stone-100" : ""}`}
        onClick={() => onDocumentSelect(doc)}
      >
        {/* Checkbox */}
        <div
          className="p-2 flex items-center justify-center"
          onClick={(e) => {
            e.stopPropagation();
            onCheckboxToggle(documentId, e);
          }}
        >
          <Checkbox checked={isChecked} />
        </div>

        {/* Title */}
        <div className="p-2 flex flex-col gap-1 cursor-pointer">
          <div className="flex items-center gap-3">
            <div
              className={`inline-flex items-center justify-center px-2 py-1 rounded text-xs font-medium border ${getFileTypeBadgeColor(fileType)}`}
            >
              {fileType}
            </div>
            <span className="text-sm truncate">{fileName}</span>
          </div>
          {failedMessage && (
            <div
              className={`flex items-center gap-2 ml-[52px] border rounded px-2 py-1 ${
                failedMessage.severity === "error"
                  ? "bg-red-50 border-red-200"
                  : "bg-yellow-50 border-yellow-200"
              }`}
            >
              <AlertCircle
                className={`h-3 w-3 shrink-0 ${
                  failedMessage.severity === "error"
                    ? "text-red-600"
                    : "text-yellow-600"
                }`}
              />
              <span
                className={`text-xs ${
                  failedMessage.severity === "error"
                    ? "text-red-800"
                    : "text-yellow-800"
                }`}
              >
                {failedMessage.message}
              </span>
            </div>
          )}
        </div>

        {/* Sample Match */}
        {procedureBasis === "sample" && (
          <div className="p-2 flex flex-col gap-1">
            <div onClick={(e) => e.stopPropagation()}>
              <Multiselect
                options={sampleOptions}
                value={uniqueSampleValues}
                onValueChange={(selectedIds: string[]) =>
                  onSampleChange(documentId, selectedIds)
                }
                placeholder="Select"
                color="green"
                multipleSelectedColor="blue"
                className="w-[140px]"
              />
            </div>
            {/* Under the control that sets it, in the column that already asks
                which samples hold this file — the count is the same answer read
                against the table's total. Gated with the analytics columns
                because it exists to report an analytics input's reach, and a
                table with no analytics column has nothing to report it for. */}
            {analytics !== undefined && boundDocumentId !== null && (
              <SampleReachCount
                sourceRows={auditSession?.sourceRows ?? []}
                documentId={boundDocumentId}
                targets={analytics.targets}
                flagged={doc.isAnalyticsInput === true}
                hasMatchEditor
              />
            )}
          </div>
        )}

        {analytics !== undefined &&
          (boundDocumentId === null ? (
            /* No id to name this file to a binding, so neither control has a
               subject to act on. Empty cells rather than none, because the grid
               is one track list shared with the header and a row that skipped
               them would slide every column after them one place left. */
            <>
              <div className="p-2" />
              <div className="p-2" />
            </>
          ) : (
            <>
              {/* For Analytics Test */}
              <div className="p-2">
                <div onClick={(e) => e.stopPropagation()}>
                  <AnalyticsInputToggle
                    flagged={doc.isAnalyticsInput === true}
                    flagKnown={doc.isAnalyticsInput !== undefined}
                    documentName={fileName}
                    documentFileType={doc.fetchableDocument?.fileType}
                    pending={analytics.isPendingFor(boundDocumentId)}
                    bindingWriteOpen={analytics.pending}
                    heldReason={analytics.writesHeldReason}
                    onChange={(next) =>
                      analytics.onSetAnalyticsInput(boundDocumentId, next)
                    }
                  />
                </div>
              </div>

              {/* Workflow Parameters */}
              <AnalyticsParametersCell
                documentId={boundDocumentId}
                documentName={fileName}
                isAnalyticsInput={doc.isAnalyticsInput}
                targets={analytics.targets}
                documentNames={documentNames}
                feedsState={analytics.feedsState}
                deletedColumnNames={analytics.deletedColumnNames}
                pending={analytics.pending}
                writesHeldReason={analytics.writesHeldReason}
                onAssign={analytics.onAssign}
                onClear={analytics.onClear}
              />
            </>
          ))}

        {/* Document Category - Multiselect Popover */}
        <div className="p-2">
          <div onClick={(e) => e.stopPropagation()}>
            <Multiselect
              options={categoryOptions}
              value={currentAssignments}
              onValueChange={(selectedIds: string[]) =>
                onCategoryChange(documentId, selectedIds)
              }
              placeholder="Select"
              color="yellow"
              className="w-[140px]"
            />
          </div>
        </div>

        {/* Actions */}
        {onRemoveDocument && (
          <div className="p-2 flex items-center justify-center">
            <div
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <Button
                variant="ghost"
                size="icon"
                aria-label="Remove document"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsConfirmOpen(true);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>

            <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
              <DialogContent
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <DialogHeader>
                  <DialogTitle>Remove document?</DialogTitle>
                  <DialogDescription>
                    This will remove "{fileName}" from the session. This action
                    cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsConfirmOpen(false);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    disabled={isDeleting}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete();
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    loading={isDeleting}
                  >
                    Delete
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison for memo
    return (
      prevProps.doc.documentId === nextProps.doc.documentId &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.isChecked === nextProps.isChecked &&
      prevProps.doc.fetchableDocument?.documentAssignments ===
        nextProps.doc.fetchableDocument?.documentAssignments &&
      prevProps.doc.fetchableDocument?.state ===
        nextProps.doc.fetchableDocument?.state &&
      prevProps.doc.fetchableDocument?.isParsed ===
        nextProps.doc.fetchableDocument?.isParsed &&
      prevProps.doc.isAnalyticsInput === nextProps.doc.isAnalyticsInput &&
      prevProps.analytics === nextProps.analytics &&
      prevProps.documentNames === nextProps.documentNames &&
      // The sample count is read against the table's own rows, and no other
      // prop compared here carries them: a sample added to or removed from the
      // table changes the count on every row while leaving each row's own
      // matches untouched.
      prevProps.auditSession?.sourceRows ===
        nextProps.auditSession?.sourceRows &&
      prevProps.sampleValues.length === nextProps.sampleValues.length &&
      prevProps.sampleValues.every(
        (val, i) => val === nextProps.sampleValues[i],
      )
    );
  },
);

export default DocumentRow;
